import { Fragment, useCallback, useEffect, useState } from 'react';
import cards from '~/assets/icons/cards_2.svg';
import coin from '~/assets/icons/coin.svg';
import percent from '~/assets/icons/percent.svg';
import transferIcon from '~/assets/icons/transfer.svg';
import userRound from '~/assets/icons/user_round.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { BankOperationType, Currency } from '~/shared';
import {
	setTransferCardNumber,
	setTransferValue,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CardInput } from '../CardInput';
import { MoneyButton } from '../MoneyButton';
import { MoneyInput } from '../MoneyInput';
import { SectionHeading } from '../SectionHeading';
import {
	StyledButtonContent,
	StyledContainer,
	StyledMoneyButtonsContainer,
	StyledMoneyButtonsFirstRow,
	StyledMoneyButtonsSecondRow,
	StyledSectionContainer,
	StyledSummaryContainer,
} from './TransferMoney.styled';

const TransferMoney: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const cardNumber = useLayoutSelector(
		(state) => state.bank.transferCardNumber,
	);
	const amount = useLayoutSelector((state) => state.bank.transferValue);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [cardOwner, setCardOwner] = useState<string | null>(null);
	const [isError, setIsError] = useState<boolean>(false);

	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const fee = useObservable(
		rage.bank.getTransferFee(BankOperationType.TRANSFER),
	);

	const transfer = useCallback(async () => {
		setIsLoading(true);
		try {
			if (cardNumber) {
				await rage.bank.transfer(cardNumber, amount);
			}
		} catch (e) {
			// @TODO: какой-то вывод ошибки
		} finally {
			setIsLoading(false);
		}
	}, [cardNumber, amount]);

	useEffect(() => {
		if (cardNumber.length < 16) {
			setCardOwner(null);
			setIsError(false);
		}

		if (cardNumber.length === 16) {
			setIsError(false);
			rage.bank
				.getCardOwner(cardNumber)
				.then((value) => {
					if (value) {
						setCardOwner(value);
					} else {
						setIsError(true);
						setCardOwner(null);
					}
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [cardNumber]);

	return (
		<StyledContainer>
			<StyledSectionContainer>
				<SectionHeading icon={cards} text={'Введите номер карты'} />
				<CardInput
					value={cardNumber}
					onChange={(value) => dispatch(setTransferCardNumber(value))}
					owner={cardOwner}
					error={isError}
				/>
			</StyledSectionContainer>
			<StyledSectionContainer>
				<SectionHeading icon={coin} text={'Введите сумму перевода'} />
				<MoneyInput
					value={amount}
					changeHandler={(value: number) => dispatch(setTransferValue(value))}
					currency={Currency.Ruble}
				/>
				<StyledMoneyButtonsContainer>
					<StyledMoneyButtonsFirstRow>
						<MoneyButton
							text={'1 000'}
							onClick={() => dispatch(setTransferValue(amount + 1000))}
						/>
						<MoneyButton
							text={'10 000'}
							onClick={() => dispatch(setTransferValue(amount + 10000))}
						/>
						<MoneyButton
							text={'100 000'}
							onClick={() => dispatch(setTransferValue(amount + 100000))}
						/>
					</StyledMoneyButtonsFirstRow>
					<StyledMoneyButtonsSecondRow>
						<MoneyButton
							text={'1 000 000'}
							onClick={() => dispatch(setTransferValue(amount + 1000000))}
						/>
						<MoneyButton
							text={'10 000 000'}
							onClick={() => dispatch(setTransferValue(amount + 10000000))}
						/>
					</StyledMoneyButtonsSecondRow>
					<MoneyButton
						text={'100 000 000'}
						onClick={() => dispatch(setTransferValue(amount + 100000000))}
					/>
				</StyledMoneyButtonsContainer>
			</StyledSectionContainer>
			<StyledSummaryContainer>
				{cardOwner && (
					<Fragment>
						<Text>Имя получателя</Text>
						<Text>{cardOwner}</Text>
						<img src={userRound} width={14} height={14} />
					</Fragment>
				)}
				<Text>С вас спишется</Text>
				<Text>{Math.ceil(amount + amount * fee).toLocaleString('ru-RU')}</Text>
				<CurrencyIcon icon={rubleIcon} size="sm" />
				{fee ? (
					<Fragment>
						<Text>Комиссия банка</Text>
						<Text>{(fee * 100).toLocaleString('ru-RU')}</Text>
						<img src={percent} width={14} height={14} />
					</Fragment>
				) : null}
			</StyledSummaryContainer>
			<Button
				color={ButtonColors.Select2}
				height={ButtonHeight.Height60}
				disabled={
					isLoading || amount === 0 || cardNumber.length !== 16 || !cardOwner
				}
				onClick={transfer}
				style={{ position: 'absolute', bottom: 0, width: '100%', fontSize: 18 }}
			>
				<StyledButtonContent>
					{cardNumber.length === 16 && amount !== 0 && cardOwner && (
						<img src={transferIcon} width={20} height={20} />
					)}
					<Text>
						{cardNumber.length !== 16 || !cardOwner
							? 'Введите номер карты'
							: amount
							? 'Перевести деньги'
							: 'Введите сумму перевода'}
					</Text>
				</StyledButtonContent>
			</Button>
		</StyledContainer>
	);
};

export { TransferMoney };
